import React, { useContext, useEffect, useState } from "react";

import {
  Checkbox,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Clipboard } from "react-feather";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { Asset } from "@safetyculture/s12-apis-connect-web/s12/assets/admin/v1/asset_pb";

import { AssetReadingConfig, Odometer, Usage } from "./data-loader-dialog";
import { listAssets } from "api/endpoints/assets";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

interface AssetListTableProps {
  assetList: AssetReadingConfig[];
  setAssetList: (newAssetList: AssetReadingConfig[]) => void;
}

const AssetListTable = ({ assetList, setAssetList }: AssetListTableProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const [orgAsset, setOrgAsset] = useState<Asset[]>([]);
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { showError } = useContext(SnackbarContext);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const asyncFunction = async () => {
      setIsLoading(true);
      listAssets(
        {
          orgId: roleId,
          pageSize: 100,
        },
        callOptionsHeaders(regionCode, roleId),
      )
        .then((resp) => {
          setOrgAsset(resp.assets);
        })
        .catch(showError)
        .finally(() => setIsLoading(false));
    };
    asyncFunction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const handleAssetSelect = (asset: Asset) => {
    let newAssetList = [...assetList];
    if (assetList.find((a) => a.assetId === asset.id)) {
      newAssetList = assetList.filter((a) => a.assetId !== asset.id);
    } else {
      newAssetList.push({
        assetId: asset.id,
        includeOdometer: false,
        odometer: Odometer.ODOMETER_UNIT_KILOMETER,
        includeUsage: false,
        usage: Usage.HOUR,
      } as AssetReadingConfig);
    }
    setAssetList(newAssetList);
  };

  const handleAssetSelectAll = () => {
    if (orgAsset.length === assetList.length) {
      setAssetList([]);
      return;
    }
    setAssetList(
      orgAsset.map((value) => {
        return {
          assetId: value.id,
          includeOdometer: false,
          odometer: Odometer.ODOMETER_UNIT_KILOMETER,
          includeUsage: false,
          usage: Usage.HOUR,
        } as AssetReadingConfig;
      }),
    );
  };

  const renderAssetRow = (asset: Asset) => {
    const assetId = asset.id || "";
    const assetCode = asset.code || "";
    const assetType = asset.type;
    return (
      <TableRow data-testid={`table-row-${assetId}`} key={assetId}>
        <TableCell>
          <Checkbox
            data-testid={`asset-select-${assetId}`}
            color="primary"
            checked={!!assetList.find((a) => a.assetId === asset.id)}
            onClick={() => handleAssetSelect(asset)}
            inputProps={{
              "aria-label": "select asset",
            }}
          />
        </TableCell>
        <TableCell>
          <Typography
            color="primary"
            fontWeight="bold"
            variant="subtitle1"
            data-testid={`asset-code-${assetId}`}
          >
            {assetCode}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            color="primary"
            fontWeight="bold"
            variant="subtitle1"
            data-testid={`asset-type-${assetId}`}
          >
            {assetType?.name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            align="left"
            sx={classes.clickable}
            noWrap={true}
            data-testid={`asset-id-${assetId}`}
          >
            {assetId}
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              className="position-icon"
            />
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Switch
            id={`asset-include-odometer-switch-${assetId}`}
            data-testid={`asset-include-odometer-switch-${assetId}`}
            checked={
              assetList.find((a) => a.assetId === assetId)
                ? assetList.find((a) => a.assetId === assetId)?.includeOdometer
                : false
            }
            onChange={() => {
              const assetToUpdate = assetList.find(
                (a) => a.assetId === assetId,
              );
              if (!!assetToUpdate) {
                assetToUpdate.includeOdometer = !assetToUpdate.includeOdometer;
                setAssetList(assetList);
              }
            }}
          />{" "}
        </TableCell>
        <TableCell align="center">
          <Select
            disabled={
              assetList.find((a) => a.assetId === assetId)
                ? !assetList.find((a) => a.assetId === assetId)?.includeOdometer
                : true
            }
            defaultValue={Odometer.ODOMETER_UNIT_KILOMETER}
            displayEmpty
            labelId={`asset-odometer-unit-selector-${assetId}`}
            id={`asset-odometer-unit-selector-${assetId}`}
            data-testid={`asset-odometer-unit-selector-${assetId}`}
            value={
              assetList.find((a) => a.assetId === assetId)
                ? assetList.find((a) => a.assetId === assetId)?.odometer
                : Odometer.ODOMETER_UNIT_KILOMETER
            }
            onChange={(e) => {
              const assetToUpdate = assetList.find(
                (a) => a.assetId === assetId,
              );
              if (!!assetToUpdate) {
                assetToUpdate.odometer = e.target.value as Odometer;
                setAssetList(assetList);
              }
            }}
          >
            <MenuItem
              value={Odometer.ODOMETER_UNIT_KILOMETER}
              id="odometer-select-kilometer"
              data-testid="odometer-select-kilometer"
            >
              <em>Kilometer</em>
            </MenuItem>
            <MenuItem
              value={Odometer.ODOMETER_UNIT_MILES}
              id="odometer-select-miles"
              data-testid="odometer-select-miles"
              data-anchor="hello"
            >
              Miles
            </MenuItem>
          </Select>
        </TableCell>
        <TableCell align="center">
          <Switch
            checked={
              assetList.find((a) => a.assetId === assetId)
                ? assetList.find((a) => a.assetId === assetId)?.includeUsage
                : false
            }
            id={`asset-include-usage-switch-${assetId}`}
            data-testid={`asset-include-usage-switch-${assetId}`}
            onChange={() => {
              const assetToUpdate = assetList.find(
                (a) => a.assetId === assetId,
              );
              if (!!assetToUpdate) {
                assetToUpdate.includeUsage = !assetToUpdate.includeUsage;
                setAssetList(assetList);
              }
            }}
          />{" "}
        </TableCell>
        <TableCell align="center">
          <Select
            disabled={
              assetList.find((a) => a.assetId === assetId)
                ? !assetList.find((a) => a.assetId === assetId)?.includeUsage
                : true
            }
            displayEmpty
            defaultValue={Usage.HOUR}
            labelId={`asset-usage-unit-selector-${assetId}`}
            id={`asset-usage-unit-selector-${assetId}`}
            data-testid={`asset-usage-unit-selector-${assetId}`}
            value={
              assetList.find((a) => a.assetId === assetId)
                ? assetList.find((a) => a.assetId === assetId)?.usage
                : Usage.HOUR
            }
            onChange={(e) => {
              const assetToUpdate = assetList.find(
                (a) => a.assetId === assetId,
              );
              if (!!assetToUpdate) {
                assetToUpdate.usage = e.target.value as Usage;
                setAssetList(assetList);
              }
            }}
          >
            <MenuItem value={Usage.HOUR} id="usage-select-hour">
              <em>Hour</em>
            </MenuItem>
            <MenuItem value={Usage.MINUTES} id="usage-select-minute">
              <em>Minute</em>
            </MenuItem>
          </Select>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Grid
      spacing={4}
      container
      direction="row"
      alignItems="center"
      paddingTop={1}
      data-testid="asset-list-table"
    >
      <Grid item xs={12}>
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer
            sx={{
              maxHeight: 440,
              border: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      color="primary"
                      data-testid={"asset-select-all"}
                      checked={orgAsset.length === assetList.length}
                      onClick={handleAssetSelectAll}
                    />
                    <b>Select All</b>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sort by name">
                      <b>Asset Code</b>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <b>Asset Type</b>
                  </TableCell>
                  <TableCell>
                    <b>Asset Id</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Include Odometer</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Unit for Odometer</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Include Runtime</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Unit for Runtime</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  orgAsset.map((asset) => renderAssetRow(asset))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AssetListTable;
